import { z } from "zod";

/**
 * What the parser is allowed to say about a message.
 *
 * The parser EXTRACTS ONLY. It never decides whether to trade, never fixes a
 * contradiction, never picks which open trade a follow-up refers to. It reports what the
 * message says and flags what looks wrong. Deciding is the decider's job, with the full
 * account and database state that this stage deliberately does not have.
 *
 * Shape note: a message carries a LIST of intents, not one. The single most common
 * follow-up in the live corpus is "Take profit 1 reached move sl to entry or near it to
 * manage risk" — simultaneously a result claim and a management instruction. A
 * discriminated union on a single `kind` would force the model to throw half of that away.
 */

export const signalDirection = z.enum(["buy", "sell"]);
export type SignalDirection = z.infer<typeof signalDirection>;

/**
 * Machine-actionable ambiguity, as a closed set rather than free text, so the decider can
 * branch on it and we can count occurrences per channel over time.
 */
export const parseIssue = z.enum([
  /** TPs and SL contradict the stated direction (a buy whose targets sit below entry). */
  "direction_conflicts_with_levels",
  /** The prices could not belong to the named instrument. */
  "implausible_price_for_symbol",
  "missing_stop_loss",
  "missing_take_profit",
  "unclear_symbol",
  /** Reads like a correction of an earlier message. */
  "possible_correction",
  "possible_typo",
  /** Levels are inconsistent with each other, in any direction. */
  "levels_inconsistent",
]);
export type ParseIssue = z.infer<typeof parseIssue>;

const priceSchema = z.number().positive().finite();

/** A new trade setup. */
export const newSignalIntent = z.object({
  type: z.literal("new_signal"),
  /** Verbatim as written — "nzd/jpy", "GOLD", "XAUUSD". Resolution happens in code. */
  symbolRaw: z.string().min(1).max(40),
  direction: signalDirection,
  /**
   * MARKET unless the message says "limit" or "stop".
   *
   * This rule is load-bearing and hard-won: channels write "EURCHF @ 0.9636~ SELL", where
   * the price is a reference for where the market was, not a resting order. Reading that
   * as a limit means never filling, or filling somewhere else entirely.
   */
  entryType: z.enum(["market", "limit", "stop"]),
  /** The stated price, even for a market entry, where it is a reference only. */
  entryPrice: priceSchema.nullable(),
  stopLoss: priceSchema.nullable(),
  /** In the order written. This channel posts three; others post one or none. */
  takeProfits: z.array(priceSchema).max(5),
});

/** "Take profit 1 was hit", "SL hit", "closed at breakeven". A claim, not an instruction. */
export const resultClaimIntent = z.object({
  type: z.literal("result_claim"),
  level: z.enum(["tp1", "tp2", "tp3", "sl", "be", "unspecified"]),
  /** Named if the message names it; the resolver decides what it refers to. */
  symbolRaw: z.string().max(40).nullable(),
});

/** "Move SL to entry", "SL to BE", "move stop to 1.0850". */
export const modifyStopIntent = z.object({
  type: z.literal("modify_sl"),
  /** `entry` and `breakeven` mean the same thing in practice and both appear. */
  target: z.enum(["breakeven", "entry", "price"]),
  /** Only when target is `price`. */
  price: priceSchema.nullable(),
  symbolRaw: z.string().max(40).nullable(),
});

export const modifyTakeProfitIntent = z.object({
  type: z.literal("modify_tp"),
  takeProfits: z.array(priceSchema).min(1).max(5),
  symbolRaw: z.string().max(40).nullable(),
});

/** "Close it", "close eurcad", "closing half". */
export const closeIntent = z.object({
  type: z.literal("close"),
  /** `partial` only when the message actually says so. */
  scope: z.enum(["all", "partial"]),
  percent: z.number().positive().max(100).nullable(),
  symbolRaw: z.string().max(40).nullable(),
});

/** Delete a pending order that has not filled. Distinct from closing a live position. */
export const cancelIntent = z.object({
  type: z.literal("cancel"),
  symbolRaw: z.string().max(40).nullable(),
});

/** Chatter, analysis, marketing, greetings, results bragging. Most messages are this. */
export const noiseIntent = z.object({
  type: z.literal("noise"),
});

/** Recognisably an instruction, but not confidently readable. Never a guess. */
export const ambiguousIntent = z.object({
  type: z.literal("ambiguous"),
  question: z.string().min(5).max(300),
});

/**
 * `z.union`, not `z.discriminatedUnion`.
 *
 * They are equivalent for our purposes in TypeScript — narrowing on the literal `type`
 * field works either way — but they compile to different JSON Schema, and OpenAI's
 * structured-output mode rejects the `oneOf` that `discriminatedUnion` produces:
 *
 *   Invalid schema for response_format: 'oneOf' is not permitted
 *
 * `union` emits `anyOf`, which is permitted. Found by running the eval, where it failed
 * 21 out of 21 fixtures.
 */
export const parsedIntent = z.union([
  newSignalIntent,
  resultClaimIntent,
  modifyStopIntent,
  modifyTakeProfitIntent,
  closeIntent,
  cancelIntent,
  noiseIntent,
  ambiguousIntent,
]);
export type ParsedIntent = z.infer<typeof parsedIntent>;

export const parsedMessage = z.object({
  /**
   * One to three. Multiple because a single message often both claims a result and gives
   * an instruction. `noise` must be the only element when present.
   */
  intents: z.array(parsedIntent).min(1).max(3),
  confidence: z.number().min(0).max(1),
  issues: z.array(parseIssue),
  /** Short free text for the audit trail. Never parsed by code. */
  notes: z.string().max(600),
});
export type ParsedMessage = z.infer<typeof parsedMessage>;

/** Bump when the prompt changes so old messages can be re-run and compared. */
export const PARSER_PROMPT_VERSION = "2026-08-09.2";

export function firstIntentOf<T extends ParsedIntent["type"]>(
  parsed: ParsedMessage,
  type: T,
): Extract<ParsedIntent, { type: T }> | undefined {
  return parsed.intents.find((intent): intent is Extract<ParsedIntent, { type: T }> =>
    intent.type === type,
  );
}

export function isActionable(parsed: ParsedMessage): boolean {
  return parsed.intents.some((intent) => intent.type !== "noise");
}
